import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminSchedule } from "@/components/AdminSchedule";
import { UsersThree, CurrencyDollar, FileText, Plus, Trash, DownloadSimple, PencilSimple, CalendarBlank, PaperPlaneTilt, ArrowsClockwise, Bank, UserCheck, CheckCircle, MinusCircle } from "@phosphor-icons/react";

const money = (n) => `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const PAYOUT_STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-800", in_transit: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-800",
};

const METHOD_LABEL = { zelle: "Zelle", check: "Check", cash: "Cash", other: "Other" };
const METHOD_OPTS = [["zelle", "Zelle"], ["check", "Check (paper)"], ["cash", "Cash"], ["other", "Other"]];

function StepIcon({ done }) {
  return done
    ? <CheckCircle size={22} weight="fill" className="text-emerald-500 inline" data-testid="step-done" />
    : <MinusCircle size={22} className="text-muted-foreground/40 inline" data-testid="step-pending" />;
}

export default function AdminDashboard() {
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [tax, setTax] = useState(null);
  const [frequency, setFrequency] = useState("all");
  const [periods, setPeriods] = useState(null);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [openReqCount, setOpenReqCount] = useState(0);
  const [connectRows, setConnectRows] = useState([]);
  const [connectLoading, setConnectLoading] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payouts, setPayouts] = useState({});
  const [balance, setBalance] = useState(null);
  const [topupAmt, setTopupAmt] = useState("");
  const [toppingUp, setToppingUp] = useState(false);
  const [onboarding, setOnboarding] = useState([]);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "employees";
  const setTab = (v) => setParams(v === "employees" ? {} : { tab: v });
  const year = new Date().getFullYear();

  const loadRequestCount = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/schedule-requests");
      setOpenReqCount(data.filter((r) => r.status !== "resolved").length);
    } catch {
      /* silent */
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [emp, pay, t] = await Promise.all([
        api.get("/employees"),
        api.get("/payroll/summary"),
        api.get(`/tax/1099/summary?year=${year}`),
      ]);
      setEmployees(emp.data);
      setPayroll(pay.data);
      setTax(t.data);
    } catch {
      toast.error("Could not load admin data. Please refresh.");
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRequestCount(); }, [loadRequestCount, tab]);

  const loadConnect = useCallback(async () => {
    setConnectLoading(true);
    try { setConnectRows((await api.get("/admin/connect/status")).data); } catch { /* silent */ }
    finally { setConnectLoading(false); }
  }, []);
  const loadBalance = useCallback(async () => {
    try { setBalance((await api.get("/admin/balance")).data); } catch { setBalance(null); }
  }, []);
  useEffect(() => { if (tab === "payroll") { loadConnect(); loadBalance(); } }, [tab, loadConnect, loadBalance]);

  const [payoutHistory, setPayoutHistory] = useState(null);
  const loadPayoutHistory = useCallback(async () => {
    try { setPayoutHistory((await api.get("/admin/payouts/history")).data.payouts || []); }
    catch { setPayoutHistory([]); }
  }, []);
  useEffect(() => { if (tab === "payroll") loadPayoutHistory(); }, [tab, loadPayoutHistory]);

  const [manualTarget, setManualTarget] = useState(null);
  const submitManual = async ({ method, amount, reference }) => {
    const t = manualTarget;
    const { data } = await api.post("/admin/payroll/manual-payout", {
      user_id: t.user_id, start: t.start, end: t.end, method, amount: parseFloat(amount),
      reference: reference || null,
    });
    toast.success(`Recorded ${money(data.amount)} to ${data.name} via ${data.method}.`);
    setManualTarget(null);
    loadPayoutHistory();
  };

  const loadOnboarding = useCallback(async () => {
    try { setOnboarding((await api.get("/admin/onboarding")).data); } catch { /* silent */ }
  }, []);
  useEffect(() => { if (tab === "employees") loadOnboarding(); }, [tab, loadOnboarding]);

  const doTopup = async () => {
    const amt = parseFloat(topupAmt);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than $0"); return; }
    setToppingUp(true);
    try {
      const { data } = await api.post("/admin/balance/topup", { amount: amt });
      toast.success(`Top-up of $${data.amount.toFixed(2)} started (${data.status}). Funds arrive in 1–3 business days.`);
      setTopupAmt("");
      loadBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Top-up failed");
    } finally {
      setToppingUp(false);
    }
  };

  const payPeriod = async (p) => {
    setPaying(p.start);
    try {
      const pre = (await api.get(`/admin/payroll/preview?start=${p.start}&end=${p.end}`)).data;
      if (pre.payable_count === 0) {
        toast.info(pre.not_ready_count > 0
          ? `No payout-ready contractors yet — ${pre.not_ready_count} still need to finish direct-deposit setup.`
          : "No payable amounts for this period.");
        setPaying(null);
        return;
      }
      if (!pre.sufficient) {
        toast.error(`Balance ${money(pre.balance_available)} won't cover ${money(pre.total_due)} for ${pre.payable_count} contractor(s). Top up ${money(pre.shortfall)} first.`, { duration: 8000 });
        setTopupAmt(pre.shortfall.toFixed(2));
        loadBalance();
        setPaying(null);
        return;
      }
      const { data } = await api.post("/admin/payroll/pay", { start: p.start, end: p.end });
      const paid = data.results.filter((r) => ["pending", "in_transit", "paid"].includes(r.status)).length;
      const blocked = data.results.filter((r) => r.status === "not_enabled").length;
      const failed = data.results.filter((r) => r.status === "failed").length;
      setPayouts((prev) => ({ ...prev, [p.start]: data.results }));
      if (paid) toast.success(`Initiated ACH payouts for ${paid} contractor(s).`);
      if (blocked) toast.warning(`${blocked} contractor(s) haven't finished direct-deposit setup.`);
      if (failed) toast.error(`${failed} payout(s) failed — see details.`);
      loadBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payout run failed");
    } finally {
      setPaying(null);
    }
  };

  useEffect(() => {
    if (frequency === "all") { setPeriods(null); return; }
    setPeriodsLoading(true);
    api.get(`/payroll/periods?frequency=${frequency}&year=${year}`)
      .then((r) => setPeriods(r.data.periods))
      .catch(() => toast.error("Could not load pay periods"))
      .finally(() => setPeriodsLoading(false));
  }, [frequency, year]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/payroll/export?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadPdf = async (empId, name) => {
    try {
      const res = await api.get(`/tax/1099/pdf?employee_id=${empId}&year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `1099-NEC-${name.replace(/ /g, "_")}-${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`1099-NEC for ${name} downloaded`);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  const emailOne = async (empId, name) => {
    toast.loading(`Emailing 1099-NEC to ${name}…`, { id: "email1099" });
    try {
      const { data } = await api.post(`/tax/1099/email?employee_id=${empId}&year=${year}`);
      toast.success(`1099-NEC emailed to ${data.to}`, { id: "email1099" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed", { id: "email1099" });
    }
  };

  const emailAll = async () => {
    toast.loading("Emailing 1099-NEC to all contractors…", { id: "emailall" });
    try {
      const { data } = await api.post(`/tax/1099/email-all?year=${year}`);
      if (data.sent === 0 && data.failed > 0) {
        toast.error(`All ${data.failed} email(s) failed to send`, { id: "emailall" });
      } else if (data.failed > 0) {
        toast.warning(`Sent ${data.sent} · ${data.failed} failed`, { id: "emailall" });
      } else {
        toast.success(`1099-NEC emailed to ${data.sent} contractor(s)`, { id: "emailall" });
      }
    } catch {
      toast.error("Bulk email failed", { id: "emailall" });
    }
  };

  const removeEmp = async (id) => {
    await api.delete(`/employees/${id}`);
    toast.success("Employee removed");
    load();
  };

  const paidMap = {};
  (payoutHistory || []).forEach((x) => { paidMap[`${x.period_start}__${x.user_id}`] = x; });

  return (
    <Layout title="Admin Console">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Stat label="Employees" value={employees.length} icon={<UsersThree size={18} />} />
        <Stat label={`${year} Payroll`} value={money(payroll?.total_pay)} icon={<CurrencyDollar size={18} />} mono />
        <Stat label={`${year} Total Hours`} value={(payroll?.total_hours ?? 0).toFixed(2)} icon={<CurrencyDollar size={18} />} mono />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-muted/60 p-1">
          <TabsTrigger value="employees" className="rounded-none" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-none relative" data-testid="tab-schedule">
            Schedule
            {openReqCount > 0 && (
              <span className="ml-2 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold text-white bg-destructive rounded-full" data-testid="schedule-request-badge">
                {openReqCount > 9 ? "9+" : openReqCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="payroll" className="rounded-none" data-testid="tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="tax" className="rounded-none" data-testid="tab-tax">1099 Forms</TabsTrigger>
        </TabsList>

        {/* Employees */}
        <TabsContent value="employees" className="mt-6">
          <div className="flex justify-end mb-4">
            <AddEmployeeDialog onDone={load} />
          </div>

          {onboarding.length > 0 && (
            <Card className="rounded-2xl border-2 border-border p-0 mb-4 overflow-x-auto" data-testid="onboarding-card">
              <div className="flex items-center gap-2 label-caps px-5 py-3 border-b border-border"><UserCheck size={14} /> Onboarding Progress</div>
              <table className="w-full min-w-[560px] text-sm" data-testid="onboarding-table">
                <thead>
                  <tr className="label-caps text-left border-b border-border bg-muted/40">
                    <th className="px-5 py-2.5 font-semibold">Contractor</th>
                    <th className="px-3 py-2.5 font-semibold text-center">Activated</th>
                    <th className="px-3 py-2.5 font-semibold text-center">W-9</th>
                    <th className="px-3 py-2.5 font-semibold text-center">Direct Deposit</th>
                    <th className="px-5 py-2.5 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {onboarding.map((o, i) => (
                    <tr key={o.user_id} className={i % 2 ? "bg-muted/20" : ""} data-testid={`onboarding-row-${i}`}>
                      <td className="px-5 py-3 font-semibold">{o.name}</td>
                      <td className="px-3 py-3 text-center"><StepIcon done={o.activated} /></td>
                      <td className="px-3 py-3 text-center"><StepIcon done={o.w9_completed} /></td>
                      <td className="px-3 py-3 text-center"><StepIcon done={o.direct_deposit_ready} /></td>
                      <td className="px-5 py-3 text-right">
                        <span className={`text-xs font-semibold px-2.5 py-1 border rounded-full ${o.complete ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`} data-testid={`onboarding-status-${i}`}>
                          {o.complete ? "Ready to pay" : "In progress"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <Card className="rounded-none border-border p-0 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm font-mono" data-testid="employees-table">
              <thead>
                <tr className="label-caps text-left border-b border-border bg-muted/40">
                  <th className="px-6 py-3 font-semibold">Name</th>
                  <th className="px-6 py-3 font-semibold">Title</th>
                  <th className="px-6 py-3 font-semibold">Email</th>
                  <th className="px-6 py-3 font-semibold text-right">Rate</th>
                  <th className="px-6 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">No employees. Add one to get started.</td></tr>}
                {employees.map((e, i) => (
                  <tr key={e.id} className={i % 2 ? "bg-muted/20" : ""} data-testid={`employee-row-${i}`}>
                    <td className="px-6 py-3 font-semibold">{e.name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{e.title || "—"}</td>
                    <td className="px-6 py-3 text-muted-foreground">{e.email}</td>
                    <td className="px-6 py-3 text-right">${e.hourly_rate.toFixed(2)}/hr</td>
                    <td className="px-6 py-3">
                      <div className="flex justify-end gap-2">
                        <EditRateDialog emp={e} onDone={load} />
                        <ResendInviteDialog emp={e} />
                        <Button variant="ghost" size="icon" className="rounded-none h-8 w-8 text-destructive" onClick={() => removeEmp(e.id)} data-testid={`delete-employee-${i}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Schedule */}
        <TabsContent value="schedule" className="mt-6">
          <AdminSchedule onRequestsChanged={loadRequestCount} />
        </TabsContent>

        {/* Payroll */}
        <TabsContent value="payroll" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center border border-border" data-testid="frequency-toggle">
              {["all", "weekly", "biweekly"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFrequency(f)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${frequency === f ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid={`frequency-${f}`}
                >
                  {f === "all" ? "Year Total" : f}
                </button>
              ))}
            </div>
            <Button variant="outline" className="rounded-none" onClick={exportCsv} data-testid="export-csv-button">
              <DownloadSimple size={16} className="mr-2" /> Export CSV
            </Button>
          </div>

          {/* Stripe balance + one-click top-up */}
          <Card className="rounded-2xl border-2 border-border p-5 mb-4" data-testid="balance-card">
            <div className="flex items-center gap-2 label-caps mb-3"><Bank size={14} /> Payout Balance</div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="font-display font-bold text-3xl tracking-tighter" data-testid="balance-available">
                  {balance ? money(balance.available) : "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  Available{balance && balance.pending > 0 ? ` · ${money(balance.pending)} pending` : ""}
                </div>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-[11px] text-muted-foreground block mb-1">Add funds ($)</label>
                  <Input type="number" min="1" step="0.01" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)}
                    className="rounded-none w-32 h-10" placeholder="0.00" data-testid="topup-amount-input" />
                </div>
                <Button className="rounded-none h-10" onClick={doTopup} disabled={toppingUp} data-testid="topup-button">
                  {toppingUp ? "Starting…" : "Top up balance"}
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Pulls from your linked bank account into Stripe. Arrives in 1–3 business days, then you can run ACH payouts.</p>
          </Card>

          {/* Payout history */}
          <Card className="rounded-2xl border-2 border-border p-5 mb-4" data-testid="payout-history-card">
            <div className="flex items-center gap-2 label-caps mb-3"><CurrencyDollar size={14} /> Payout History</div>
            {payoutHistory === null && <p className="text-sm text-muted-foreground" data-testid="payout-history-loading">Loading payouts…</p>}
            {payoutHistory !== null && payoutHistory.length === 0 && <p className="text-sm text-muted-foreground" data-testid="payout-history-empty">No payouts have been run yet.</p>}
            {payoutHistory !== null && payoutHistory.length > 0 && (
              <div className="max-h-80 overflow-y-auto -mx-1 px-1" data-testid="payout-history-list">
                {payoutHistory.map((p) => (
                  <div key={`${p.user_id}-${p.period_start}`} className="flex items-center justify-between border-b border-border py-2.5" data-testid={`admin-payout-row-${p.user_id}-${p.period_start}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.period_start} → {p.period_end} · {money(p.amount)}</div>
                    </div>
                    <span className={`shrink-0 text-[0.6rem] uppercase tracking-wide px-2 py-1 ${PAYOUT_STATUS_STYLE[p.status] || PAYOUT_STATUS_STYLE.pending}`} data-testid={`admin-payout-status-${p.user_id}-${p.period_start}`}>
                      {(p.status || "pending").replace("_", " ")}{p.payment_method ? ` · ${METHOD_LABEL[p.payment_method] || p.payment_method}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Direct deposit readiness */}
          <Card className="rounded-2xl border-2 border-border p-5 mb-4" data-testid="dd-readiness-card">
            <div className="flex items-center gap-2 label-caps mb-3"><Bank size={14} /> Direct Deposit Readiness</div>
            {connectLoading && connectRows.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="dd-readiness-loading">Checking direct-deposit status…</p>
            ) : connectRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contractors yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="dd-readiness-list">
                {connectRows.map((c) => (
                  <span key={c.user_id} className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border ${c.payouts_enabled ? "border-primary/40 bg-primary/5 text-primary" : "border-amber-400/60 bg-amber-50 text-amber-800"}`} data-testid={`dd-ready-${c.user_id}`}>
                    {c.name} · {c.payouts_enabled ? "Ready" : c.has_account ? "Setup pending" : "Not started"}
                  </span>
                ))}
              </div>
            )}
          </Card>

          {frequency === "all" && (
            <Card className="rounded-none border-border p-0 overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm font-mono" data-testid="payroll-table">
                <thead>
                  <tr className="label-caps text-left border-b border-border bg-muted/40">
                    <th className="px-6 py-3 font-semibold">Employee</th>
                    <th className="px-6 py-3 font-semibold text-right">Rate</th>
                    <th className="px-6 py-3 font-semibold text-right">Reg Hrs</th>
                    <th className="px-6 py-3 font-semibold text-right">OT Hrs</th>
                    <th className="px-6 py-3 font-semibold text-right">Total Hrs</th>
                    <th className="px-6 py-3 font-semibold text-right">Gross Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll?.rows.map((r, i) => (
                    <tr key={r.user_id} className={i % 2 ? "bg-muted/20" : ""} data-testid={`payroll-row-${i}`}>
                      <td className="px-6 py-3 font-semibold">{r.name}</td>
                      <td className="px-6 py-3 text-right">${r.hourly_rate.toFixed(2)}</td>
                      <td className="px-6 py-3 text-right">{r.regular_hours.toFixed(2)}</td>
                      <td className="px-6 py-3 text-right text-destructive">{r.ot_hours.toFixed(2)}</td>
                      <td className="px-6 py-3 text-right">{r.total_hours.toFixed(2)}</td>
                      <td className="px-6 py-3 text-right font-bold">{money(r.total_pay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-foreground font-bold">
                    <td className="px-6 py-3">TOTAL</td>
                    <td></td>
                    <td className="px-6 py-3 text-right">{(payroll?.regular_hours ?? 0).toFixed(2)}</td>
                    <td className="px-6 py-3 text-right">{(payroll?.ot_hours ?? 0).toFixed(2)}</td>
                    <td className="px-6 py-3 text-right" data-testid="payroll-total-hours">{(payroll?.total_hours ?? 0).toFixed(2)}</td>
                    <td className="px-6 py-3 text-right" data-testid="payroll-total-pay">{money(payroll?.total_pay)}</td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}

          {frequency !== "all" && (
            <div className="space-y-4" data-testid="periods-list">
              {periodsLoading && (
                <Card className="rounded-none border-border p-10 text-center text-muted-foreground font-mono text-sm">Loading pay periods…</Card>
              )}
              {!periodsLoading && (!periods || periods.length === 0) && (
                <Card className="rounded-none border-border p-10 text-center text-muted-foreground">No pay data for {year} yet.</Card>
              )}
              {!periodsLoading && periods?.map((p, i) => (
                <Card key={i} className="rounded-none border-border p-0" data-testid={`period-${i}`}>
                  <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-muted/30">
                    <div className="flex items-center gap-2 font-mono text-sm font-semibold">
                      <CalendarBlank size={16} className="text-primary" /> {p.start} → {p.end}
                    </div>
                    <div className="font-mono text-sm flex items-center gap-6">
                      <span className="text-muted-foreground">{p.total_hours.toFixed(2)} hrs</span>
                      <span className="font-bold">{money(p.total_pay)}</span>
                      <Button size="sm" className="rounded-none" onClick={() => payPeriod(p)} disabled={paying === p.start} data-testid={`pay-period-${i}`}>
                        <Bank size={14} className="mr-1.5" /> {paying === p.start ? "Paying…" : "Pay via ACH"}
                      </Button>
                    </div>
                  </div>
                  {payouts[p.start] && (
                    <div className="px-6 py-2 bg-primary/5 border-b border-border text-xs font-mono space-y-1" data-testid={`payout-results-${i}`}>
                      {payouts[p.start].map((r, k) => (
                        <div key={k} className="flex justify-between">
                          <span>{r.name}</span>
                          <span className={r.status === "failed" || r.status === "not_enabled" ? "text-destructive" : "text-primary"}>
                            {r.status === "not_enabled" ? "setup incomplete" : r.status === "failed" ? `failed: ${r.error || ""}` : r.status} · ${r.amount?.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <table className="w-full text-sm font-mono">
                    <tbody>
                      {p.rows.map((r, j) => {
                        const pd = paidMap[`${p.start}__${r.user_id}`];
                        return (
                        <tr key={j} className={j % 2 ? "bg-muted/20" : ""}>
                          <td className="px-6 py-2">{r.name}</td>
                          <td className="px-6 py-2 text-right text-muted-foreground">{r.hours.toFixed(2)} h{r.ot_hours > 0 ? ` (OT ${r.ot_hours.toFixed(2)})` : ""}</td>
                          <td className="px-6 py-2 text-right font-bold">{money(r.pay)}</td>
                          <td className="px-6 py-2 text-right whitespace-nowrap">
                            {pd ? (
                              <span className={`text-[0.6rem] uppercase tracking-wide px-2 py-1 ${PAYOUT_STATUS_STYLE[pd.status] || PAYOUT_STATUS_STYLE.pending}`} data-testid={`period-paid-${i}-${j}`}>
                                {(pd.status || "paid").replace("_", " ")}{pd.payment_method ? ` · ${METHOD_LABEL[pd.payment_method] || pd.payment_method}` : ""}
                              </span>
                            ) : (
                              <button
                                className="text-xs text-primary hover:underline"
                                onClick={() => setManualTarget({ start: p.start, end: p.end, user_id: r.user_id, name: r.name, pay: r.pay })}
                                data-testid={`mark-paid-${i}-${j}`}
                              >
                                Mark paid manually
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* 1099 */}
        <TabsContent value="tax" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
            <div className="label-caps">Payer: {tax?.payer.name} · TIN {tax?.payer.tin}</div>
            <Button className="rounded-none" onClick={emailAll} data-testid="email-all-1099-button">
              <PaperPlaneTilt size={16} className="mr-2" /> Email All 1099-NEC
            </Button>
          </div>
          <Card className="rounded-none border-border p-0 overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm font-mono" data-testid="tax-table">
              <thead>
                <tr className="label-caps text-left border-b border-border bg-muted/40">
                  <th className="px-6 py-3 font-semibold">Recipient</th>
                  <th className="px-6 py-3 font-semibold">Email</th>
                  <th className="px-6 py-3 font-semibold">W-9</th>
                  <th className="px-6 py-3 font-semibold text-right">Box 1 · Nonemployee Comp</th>
                  <th className="px-6 py-3 font-semibold text-right">Form</th>
                </tr>
              </thead>
              <tbody>
                {tax?.rows.map((r, i) => (
                  <tr key={r.user_id} className={i % 2 ? "bg-muted/20" : ""} data-testid={`tax-row-${i}`}>
                    <td className="px-6 py-3 font-semibold">{r.name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{r.email}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold border ${r.w9_completed ? "border-primary/40 bg-primary/5 text-primary" : "border-amber-400/60 bg-amber-50 text-amber-800"}`} data-testid={`w9-status-${i}`}>
                        {r.w9_completed ? "On file" : "Pending"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-bold">{money(r.nonemployee_compensation)}</td>
                    <td className="px-6 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="rounded-none" onClick={() => downloadPdf(r.user_id, r.name)} data-testid={`download-1099-${i}`}>
                          <DownloadSimple size={14} className="mr-1.5" /> PDF
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-none" disabled={r.nonemployee_compensation <= 0} onClick={() => emailOne(r.user_id, r.name)} data-testid={`email-1099-${i}`}>
                          <PaperPlaneTilt size={14} className="mr-1.5" /> Email
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
      <ManualPayoutDialog target={manualTarget} onClose={() => setManualTarget(null)} onSubmit={submitManual} />
    </Layout>
  );
}

function ManualPayoutDialog({ target, onClose, onSubmit }) {
  const [method, setMethod] = useState("zelle");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) { setMethod("zelle"); setAmount(String((target.pay ?? 0).toFixed(2))); setReference(""); }
  }, [target]);
  const save = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter an amount greater than $0"); return; }
    setBusy(true);
    try { await onSubmit({ method, amount, reference }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record payment"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none" data-testid="manual-payout-dialog">
        <DialogHeader><DialogTitle>Record manual payment</DialogTitle></DialogHeader>
        {target && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {target.name} · {target.start} → {target.end}
            </p>
            <div>
              <Label className="label-caps">Payment method</Label>
              <select className="w-full rounded-none mt-1 h-10 border border-input bg-background px-3 text-sm" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="manual-method">
                {METHOD_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-caps">Amount ($)</Label>
              <Input type="number" min="0.01" step="0.01" className="rounded-none mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="manual-amount" />
            </div>
            <div>
              <Label className="label-caps">Reference / note (optional)</Label>
              <Input className="rounded-none mt-1" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check #1024, Zelle confirmation, etc." data-testid="manual-reference" />
            </div>
            <DialogFooter>
              <Button className="rounded-none w-full" onClick={save} disabled={busy} data-testid="manual-submit">
                {busy ? "Recording…" : "Record payment"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon, mono }) {
  return (
    <Card className="rounded-none border-border p-6">
      <div className="flex items-center gap-2 label-caps mb-3">{icon} {label}</div>
      <div className={`text-3xl font-bold ${mono ? "font-mono tabular" : "font-display tracking-tighter"}`}>{value}</div>
    </Card>
  );
}

function AddEmployeeDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", title: "", hourly_rate: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const reset = () => { setF({ name: "", email: "", title: "", hourly_rate: "" }); setResult(null); };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/employees", {
        name: f.name, email: f.email, title: f.title, hourly_rate: parseFloat(f.hourly_rate || "0"),
      });
      setResult(data);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-none" data-testid="add-employee-button"><Plus size={16} className="mr-1.5" /> Add Contractor</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none">
        {!result ? (
          <>
            <DialogHeader><DialogTitle className="font-display tracking-tighter">New Contractor</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">A temporary password will be generated and emailed to the contractor.</p>
            <div className="space-y-4 py-2">
              <div><Label className="label-caps">Name</Label><Input className="rounded-none mt-2" value={f.name} onChange={upd("name")} data-testid="emp-name-input" /></div>
              <div><Label className="label-caps">Email</Label><Input type="email" className="rounded-none mt-2" value={f.email} onChange={upd("email")} data-testid="emp-email-input" /></div>
              <div><Label className="label-caps">Title</Label><Input className="rounded-none mt-2" placeholder="e.g. Registered Nurse" value={f.title} onChange={upd("title")} data-testid="emp-title-input" /></div>
              <div><Label className="label-caps">Hourly Rate ($)</Label><Input type="number" step="0.01" className="rounded-none mt-2 font-mono" value={f.hourly_rate} onChange={upd("hourly_rate")} data-testid="emp-rate-input" /></div>
            </div>
            <DialogFooter>
              <Button className="rounded-none" disabled={busy || !f.name || !f.email} onClick={save} data-testid="save-employee-button">{busy ? "Creating…" : "Create & send invite"}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-display tracking-tighter">Contractor created</DialogTitle></DialogHeader>
            <div className="py-2 space-y-4" data-testid="create-result">
              <div className="text-sm font-mono px-3 py-2 border border-primary/40 bg-primary/5">
                ✓ Contractor account created successfully.
              </div>
              <div className={`text-sm font-mono px-3 py-2 border ${result.email_sent ? "border-primary/40 bg-primary/5" : "border-amber-400/60 bg-amber-50 text-amber-800"}`} data-testid="email-status">
                {result.email_sent
                  ? `Invite emailed to ${result.email}.`
                  : `We couldn't email the invite (${result.email_error || "unknown"}). Share the temporary password below with the contractor manually.`}
              </div>
              <div>
                <Label className="label-caps">Temporary password</Label>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 font-mono text-lg bg-muted px-3 py-2 border border-border" data-testid="temp-password-value">{result.temp_password}</code>
                  <Button variant="outline" className="rounded-none" onClick={() => { navigator.clipboard?.writeText(result.temp_password); toast.success("Copied"); }} data-testid="copy-temp-password">Copy</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">The contractor must change this on first sign-in.</p>
              </div>
            </div>
            <DialogFooter>
              <Button className="rounded-none" onClick={() => { setOpen(false); reset(); }} data-testid="close-result-button">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditRateDialog({ emp, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: emp.name, title: emp.title || "", hourly_rate: String(emp.hourly_rate) });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    await api.put(`/employees/${emp.id}`, { name: f.name, title: f.title, hourly_rate: parseFloat(f.hourly_rate || "0") });
    toast.success("Contractor updated");
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" data-testid={`edit-employee-${emp.id}`}><PencilSimple size={16} /></Button>
      </DialogTrigger>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">Edit {emp.name}</DialogTitle></DialogHeader>
        <div className="py-2 space-y-4">
          <div><Label className="label-caps">Name</Label><Input className="rounded-none mt-2" value={f.name} onChange={upd("name")} data-testid="edit-name-input" /></div>
          <div><Label className="label-caps">Title</Label><Input className="rounded-none mt-2" value={f.title} onChange={upd("title")} data-testid="edit-title-input" /></div>
          <div><Label className="label-caps">Hourly Rate ($)</Label><Input type="number" step="0.01" className="rounded-none mt-2 font-mono" value={f.hourly_rate} onChange={upd("hourly_rate")} data-testid="edit-rate-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-none" onClick={save} data-testid="save-rate-button">Update</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResendInviteDialog({ emp }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${emp.id}/resend-invite`);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" title="Resend invite / reset password" data-testid={`resend-invite-${emp.id}`}>
          <ArrowsClockwise size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none">
        {!result ? (
          <>
            <DialogHeader><DialogTitle className="font-display tracking-tighter">Resend invite</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground py-2">
              Generate a new temporary password for <strong>{emp.name}</strong> ({emp.email}) and email it to them.
              Their current password will stop working and they'll set a new one on next sign-in.
            </p>
            <DialogFooter>
              <Button className="rounded-none" disabled={busy} onClick={send} data-testid="confirm-resend-button">
                {busy ? "Sending…" : "Reset & send invite"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-display tracking-tighter">Invite reset</DialogTitle></DialogHeader>
            <div className="py-2 space-y-4" data-testid="resend-result">
              <div className="text-sm font-mono px-3 py-2 border border-primary/40 bg-primary/5">✓ New temporary password generated.</div>
              <div className={`text-sm font-mono px-3 py-2 border ${result.email_sent ? "border-primary/40 bg-primary/5" : "border-amber-400/60 bg-amber-50 text-amber-800"}`} data-testid="resend-email-status">
                {result.email_sent
                  ? `Invite emailed to ${result.email}.`
                  : `We couldn't email the invite (${result.email_error || "unknown"}). Share the temporary password below manually.`}
              </div>
              <div>
                <Label className="label-caps">Temporary password</Label>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 font-mono text-lg bg-muted px-3 py-2 border border-border" data-testid="resend-temp-password">{result.temp_password}</code>
                  <Button variant="outline" className="rounded-none" onClick={() => { navigator.clipboard?.writeText(result.temp_password); toast.success("Copied"); }} data-testid="copy-resend-password">Copy</Button>
                </div>
              </div>
            </div>
            <DialogFooter><Button className="rounded-none" onClick={() => { setOpen(false); setResult(null); }} data-testid="close-resend-button">Done</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
